import type { Optional } from '@vy/utils';
import { ListItemIcon, ListItemText, MenuItem, Stack, TextField, Typography } from '@mui/material';
import type { InvoiceOverview, SendReminderEmailRequest } from '@vy/api-definition';
import { VyIcon } from '@vy/ui-design-system';
import { getInvoiceEmailRecipients } from '@vy/services';
import { Contact } from '@vy/types';
import { isEmptyArray, isMultiple, validateNotEmptyArray, validateNotNullOrUndefined } from '@vy/utils';
import { first } from 'lodash';
import { useForm } from 'react-hook-form';
import { VyDialog } from '../../../../../../components/new-design/feedback/VyDialog';
import { VyTable } from '../../../../../../components/new-design/table/vy-table/VyTable';
import { useRemindPayment } from '../../../../../../hooks/requests/use-remind-payment';
import { useDialog } from '../../../../../../hooks/use-dialog';
import { runMutation } from '../../../../../../utils/vy-mutation';
import { getDefaultMessageBody, getDefaultMessageSubject } from './send-reminder-consts/get-default-mail';
import { INVOICE_DETAILS_TABLE_COLUMNS } from './send-reminder-consts/invoice-details-table-columns';

type SendReminderProps = {
  invoices: InvoiceOverview[];
};

export const SendReminder: React.FC<SendReminderProps> = ({ invoices }) => {
  const remindPaymentMutation = useRemindPayment();
  const { isDialogOpen, handleClose, handleOpen } = useDialog();
  const firstInvoice = first(invoices);
  validateNotNullOrUndefined(firstInvoice);

  const emails = getRecipients(firstInvoice.customer.contacts);

  const { register, handleSubmit } = useForm<SendReminderEmailRequest>({
    defaultValues: {
      customerId: firstInvoice.customer.id,
      invoiceId: firstInvoice.id,
      emails,
    },
  });

  const onSendReminder = handleSubmit(async (data) => {
    await runMutation(remindPaymentMutation, data, {
      success: `Reminder Email${isMultiple(emails) ? 's' : ''} Sent!`,
      pending: `Sending Reminder email${isMultiple(emails) ? 's' : ''}...`,
      error: `Failed to send reminder email${isMultiple(emails) ? 's' : ''}`,
    });

    handleClose();
  });

  return (
    <>
      <MenuItem onClick={handleOpen}>
        <ListItemIcon>
          <VyIcon icon="EmailClock" color="black" />
        </ListItemIcon>
        <ListItemText>Send a reminder</ListItemText>
      </MenuItem>
      <VyDialog
        label="Send a reminder"
        open={isDialogOpen}
        onClose={handleClose}
        maxWidth="md"
        ctaText="Send"
        ctaButtonProps={{
          startIcon: <VyIcon icon="Send" />,
          onClick: onSendReminder,
        }}
        cancelButtonText="Cancel"
        dialogContentProps={{ dividers: false }}
        fullWidth
        sx={{
          '& .MuiDialogContent-root': {
            overflow: 'hidden',
          },
        }}
      >
        <Stack>
          <Typography
            variant="body2"
            sx={{
              fontSize: "16px",
              fontWeight: 550
            }}>
            Invoice details
          </Typography>

          <VyTable
            tableName="send-reminder-invoices"
            columns={INVOICE_DETAILS_TABLE_COLUMNS}
            items={invoices}
            itemsCount={invoices.length}
            sx={{
              '& .MuiDataGrid-iconSeparator': {
                display: 'none',
              },
              '& .MuiTablePagination-root': {
                display: 'none',
              },
            }}
            showPagination={false}
          />

          <Stack
            spacing={0.5}
            sx={{
              mt: 2,
              width: "100%"
            }}>
            <Typography
              variant="body2"
              sx={{
                fontSize: "16px",
                fontWeight: 550
              }}>
              Add your message
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="Subject"
                fullWidth
                variant="filled"
                sx={{
                  fontSize: '16px',
                  '& .MuiInputBase-root': {
                    padding: '0px !important',
                  },
                }}
                {...register('subject')}
                defaultValue={getDefaultMessageSubject(firstInvoice.ref)}
              />

              <TextField
                label="Message"
                fullWidth
                multiline
                variant="filled"
                sx={{
                  fontSize: '16px',
                  '& .MuiInputBase-root': {
                    padding: '0px !important',
                    alignItems: 'flex-start',
                    height: '280px',
                  },
                }}
                rows={10}
                {...register('content')}
                defaultValue={getDefaultMessageBody(firstInvoice.ref, firstInvoice.customer.name)}
              />
            </Stack>
          </Stack>
        </Stack>
      </VyDialog>
    </>
  );
};

export function getRecipients(contacts: Optional<Contact[]>) {
  if (isEmptyArray(contacts)) {
    return [];
  }

  validateNotEmptyArray(contacts);

  return getInvoiceEmailRecipients(contacts);
}
